/**
 * SessionPaymentDialog — iter227
 *
 * "Who paid for this session?" modal. Triggered from the SessionRow banner
 * or the session-completion checklist. Records the payer + amount and, if
 * a member paid, reimburses them from the group fund.
 *
 * Props:
 *   open          — bool
 *   session       — session doc (id, title, date, present_ids, external_guests, venue_id)
 *   group         — group doc (members, coin_balances, session_fee)
 *   fineDefaults  — group's fine_defaults (used for session_fee prefill)
 *   venues        — array of venue docs (may be [])
 *   onClose       — () => void
 *   onSaved       — () => void  (refresh sessions list)
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2, Save, Wallet, User, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

/** Pick day/night/weekend from a venue based on the session date + start_time. */
function pickVenueFee(venue, session) {
  if (!venue) return null;
  if (!venue.has_time_bands) return venue.default_fee || 0;
  const d = session?.date ? new Date(session.date) : new Date();
  const dow = d.getDay(); // 0 Sun … 6 Sat
  const isWeekend = dow === 0 || dow === 6;
  if (isWeekend && venue.weekend_fee != null) return venue.weekend_fee;
  const t = (session?.start_time || '19:00').split(':')[0] | 0;
  const isNight = t >= 18 || t < 6;
  if (isNight && venue.night_fee != null) return venue.night_fee;
  if (!isNight && venue.day_fee != null) return venue.day_fee;
  return venue.default_fee || venue.day_fee || venue.night_fee || venue.weekend_fee || 0;
}

export default function SessionPaymentDialog({
  open, session, group, fineDefaults, venues, onClose, onSaved,
}) {
  const [paidBy, setPaidBy] = useState('group_fund');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [suggested, setSuggested] = useState(null);

  // Roster of eligible payers: attendees (present) + admins, deduplicated
  const eligiblePayers = useMemo(() => {
    if (!session || !group) return [];
    const seen = new Set();
    const out = [];
    const attendees = new Set(session.present_ids || []);
    const admins = new Set(group.admin_ids || []);
    const coAdmins = new Set(group.co_admin_ids || []);
    (group.members || []).forEach((m) => {
      if (seen.has(m.id)) return;
      seen.add(m.id);
      const label = attendees.has(m.id) ? 'attended'
        : admins.has(m.id) ? 'admin'
        : coAdmins.has(m.id) ? 'co-admin'
        : 'member';
      out.push({ id: m.id, name: m.name, label });
    });
    // Sort: attended first, then admins, then rest by name
    out.sort((a, b) => {
      const w = (x) => x.label === 'attended' ? 0 : x.label === 'admin' ? 1 : x.label === 'co-admin' ? 2 : 3;
      return w(a) - w(b) || a.name.localeCompare(b.name);
    });
    return out;
  }, [session, group]);

  // Prefill amount + payer when dialog opens or venue changes
  useEffect(() => {
    if (!open) return;
    const venue = venues?.find((v) => v.id === session?.venue_id) || null;
    const venueFee = pickVenueFee(venue, session);
    const perHead = Number(fineDefaults?.session_fee || 0);
    const heads = (session?.present_ids?.length || 0) + (session?.external_guests?.length || 0);
    const prefill = venueFee != null && venueFee > 0
      ? venueFee
      : (perHead > 0 && heads > 0 ? perHead * heads : perHead);
    setAmount(String(Math.round(prefill || 0)));
    setPaidBy('group_fund');
    setNote('');
    setSuggested(null);
    // iter228 — Fetch rotation-based suggestion. Fire-and-forget: if it fails
    // or returns null, we keep the default 'group_fund' pick.
    if (session?.id) {
      api.get(`/sessions/${session.id}/suggested-payer`)
        .then((r) => {
          const hint = r.data;
          if (hint?.payer_id) {
            setPaidBy(hint.payer_id);
            setSuggested(hint);
          }
        })
        .catch(() => {});
    }
  }, [open, session, venues, fineDefaults]);

  const linkedVenue = useMemo(
    () => venues?.find((v) => v.id === session?.venue_id) || null,
    [venues, session],
  );

  const save = async () => {
    if (!session?.id) return;
    const isComplimentary = paidBy === 'complimentary';
    const amt = isComplimentary ? 0 : Number(amount || 0);
    if (paidBy !== 'group_fund' && !isComplimentary && (!amt || amt <= 0)) {
      toast.error('Enter a positive amount for a member reimbursement');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/sessions/${session.id}/payment`, {
        paid_by: paidBy,
        amount: amt,
        note: note?.trim() || null,
        venue_id: session.venue_id || null,
      });
      toast.success(isComplimentary
        ? 'Marked complimentary — no venue cost and no session fees will be charged'
        : paidBy === 'group_fund'
          ? 'Marked as fund-paid'
          : `Reimbursed ₹${amt.toLocaleString('en-IN')} to payer`);
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="session-payment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="w-4 h-4" /> Record who paid
          </DialogTitle>
          <DialogDescription>
            Log the person who paid the venue cash for <strong>{session?.title}</strong>.
            If a member paid, the fund reimburses them; attendees still owe their per-head session fee.
          </DialogDescription>
        </DialogHeader>

        {linkedVenue && (
          <div className="text-xs bg-muted/40 border rounded-lg px-3 py-2" data-testid="session-payment-venue-hint">
            <span className="text-muted-foreground">Venue: </span>
            <span className="font-semibold">{linkedVenue.name}</span>
            {linkedVenue.has_time_bands && (
              <span className="text-muted-foreground"> · time-band pricing applied</span>
            )}
          </div>
        )}

        {suggested?.payer_name && (
          <div className="text-xs bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 flex items-center gap-2" data-testid="session-payment-rotation-hint">
            <span className="text-lg leading-none">🔁</span>
            <span>
              Pre-filled from rotation: <strong>{suggested.payer_name}</strong>
              <span className="text-muted-foreground">
                {suggested.source === 'monthly' ? ` (${suggested.month})` : ' (round-robin turn)'}
              </span>
            </span>
          </div>
        )}

        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-sm">Paid by</Label>
            <Select value={paidBy} onValueChange={setPaidBy}>
              <SelectTrigger data-testid="session-payment-paid-by">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="group_fund">
                  <span className="flex items-center gap-2">
                    <Wallet className="w-3.5 h-3.5" /> Group Fund (no reimbursement)
                  </span>
                </SelectItem>
                <SelectItem value="complimentary" data-testid="session-payment-complimentary">
                  <span className="flex items-center gap-2">
                    🎁 No cost — complimentary / free court
                  </span>
                </SelectItem>
                {eligiblePayers.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`session-payment-payer-${p.id}`}>
                    <span className="flex items-center gap-2">
                      <User className="w-3.5 h-3.5" /> {p.name}
                      <span className="text-xs uppercase text-muted-foreground">· {p.label}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-sm">Amount (₹)</Label>
            <Input
              type="number"
              min={0}
              value={paidBy === 'complimentary' ? '0' : amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={paidBy === 'group_fund' || paidBy === 'complimentary'}
              placeholder="0"
              data-testid="session-payment-amount"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {paidBy === 'complimentary'
                ? 'Free session — no reimbursement, and players will NOT be charged the per-head session fee.'
                : paidBy === 'group_fund'
                  ? 'Fund-paid sessions need no amount — attendees only pay their per-head session fee.'
                  : 'Pre-filled from venue library or session_fee × attendees. Edit if the actual venue charge differed.'}
            </p>
          </div>

          <div>
            <Label className="text-sm">Note (optional)</Label>
            <Textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. day rate + court light surcharge"
              data-testid="session-payment-note"
            />
          </div>
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => onClose?.()} disabled={saving} data-testid="session-payment-cancel">
            <X className="w-3.5 h-3.5 mr-1" /> Cancel
          </Button>
          <Button onClick={save} disabled={saving} data-testid="session-payment-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
